import { cn } from '@/lib/utils';
import { businessInitials, pickAvatarColor } from './initials';

export interface BusinessAvatarProps {
  /** Display name — used for the alt-text and as a fallback for initials. */
  name: string;
  /**
   * Up to 3 characters of initials. If omitted we derive from `name`
   * using the same algorithm the mobile adapter uses
   * (`businessInitials`). Pass an explicit value when the org row
   * carries a hand-picked nickname (e.g. "ASL").
   */
  initials?: string;
  /**
   * Hex tile colour. If omitted we hash the `id` for a stable
   * deterministic colour. Pass `org.color` when the org row already
   * carries one (preferred — it picks up the user's branding).
   */
  color?: string;
  /**
   * Required for the deterministic colour fallback. The id is used
   * as a hash seed so each org gets a stable tile colour across
   * platforms.
   */
  id: string;
  logoUrl?: string | null;
  /** Square edge length in CSS pixels. Defaults to 40. */
  size?: number;
  /** Corner radius in CSS pixels. Defaults to a 1/3.4 rounded-square. */
  radius?: number;
  /**
   * Override for the initials text size. Defaults to
   * `Math.round(size * 0.4)`, matching the mobile component.
   */
  fontSize?: number;
  className?: string;
}

/**
 * Single source of truth for organisation avatars on the web.
 *
 * Mirrors `apps/mobile/features/tally/components/BusinessAvatar.tsx`
 * — uploading a logo on mobile makes it appear here, and the
 * deterministic-colour fallback hashes from the same id so a fresh
 * org reads with the same tile colour on both surfaces.
 *
 * The fallback uses white initials on the tile colour (vs the
 * client avatar, which uses ink-coloured initials on a tinted
 * background) so the two shapes — per-org and per-client — stay
 * visually distinguishable in mixed lists.
 */
export function BusinessAvatar({
  name,
  initials,
  color,
  id,
  logoUrl,
  size = 40,
  radius,
  fontSize,
  className,
}: BusinessAvatarProps) {
  const r = radius ?? Math.round(size / 3.4);
  const text = fontSize ?? Math.round(size * 0.4);
  const tileColor = color ?? pickAvatarColor(id);
  const tileInitials = initials ?? businessInitials(name);

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden',
        className,
      )}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${r}px`,
        background: tileColor,
      }}
      aria-label={`${name} logo`}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={`${name} logo`}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
          draggable={false}
        />
      ) : (
        <span
          aria-hidden
          style={{
            color: '#fff',
            fontSize: `${text}px`,
            letterSpacing: `${-text * 0.01}px`,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {tileInitials}
        </span>
      )}
    </div>
  );
}
